/**
 * The parts both dashboards build their box tables from (D2 ruling P12): the
 * Executive Overview (overviewTables, overviewCsv, overviewChatContext) and
 * the Channel Deep-dive (channelTables, channelCsv, channelChatContext). One
 * home, so the two pages, their files and their chat contexts cannot drift.
 * - Cells: raw numbers with float noise removed, never rounded; NULL stays
 *   null, never 0; ratios and deltas are percent numbers.
 * - Headers: money ones name the currency.
 * - The chat is shown each cell as the page renders it; the file keeps the
 *   raw number, and writes "—" where a value holds nothing (rules-04 D4).
 * Pure.
 *
 * @module services/overview/boxTableParts
 */
import type { TFunction } from 'i18next';
import type { CsvCell } from '@/services/csv';
import type { DashboardCsvLabels } from '@/services/looker/lookerCsv';
import { withoutFloatNoise } from '@/services/looker/lookerNumber';
import type { MetricRow } from './breakdownTables';
import { DASH, fmtCount, fmtMoney, MONEY_METRICS, scoreLabel } from './overviewFormat';
import type { BoxStatus, BoxTable, CellKind } from './overviewTables';
import type { BoxState, CurrencyChoice } from './overviewTypes';
import type { Scorecard } from './scorecards';
import type { Trend } from './spendTrend';

// ---------------------------------------------------------------- cells

export const num = (n: number | null): CsvCell => (n === null ? null : withoutFloatNoise(n));
export const pct = (n: number | null): CsvCell => (n === null ? null : withoutFloatNoise(n * 100));
/** A header with its unit: "Spend (THB)", "CTR (%)"; no unit, the bare label. */
export const unit = (label: string, u: string | null): string => (u ? `${label} (${u})` : label);
/** The same kinds for every row. */
export const each = <R>(rows: readonly R[], k: CellKind[]): CellKind[][] => rows.map(() => k);

// ---------------------------------------------------------------- one box

/** What a ready box's builder fills in; the shell adds the id, the title and the status. */
export type Built = Pick<BoxTable, 'columns' | 'rows' | 'kinds'> & Partial<Omit<BoxTable, 'id' | 'title' | 'status'>>;

/** A box with no rows to write: its status, and the words the page shows in its place. */
export const bare = (id: string, title: string, status: BoxStatus, notes: string[] = []): BoxTable =>
  ({ id, title, status, columns: [], rows: [], kinds: [], notes });

/** A ready box is what `build` makes of its data; any other is bare, with `notes` when the page words it. */
export const shell = <T>(
  id: string, title: string, state: BoxState<T>, build: (data: T) => Built, notes: string[] = [],
): BoxTable =>
  (state.status === 'ready' ? { id, title, status: 'ok', notes: [], ...build(state.data) } : bare(id, title, state.status, notes));

// ---------------------------------------------------------------- the six metrics

/** Spend · Impressions · Clicks · CTR · CPC · CPM. */
export const METRIC_KINDS: readonly CellKind[] = ['money', 'count', 'count', 'pct', 'money', 'money'];

export const metricColumns = (t: TFunction, currency: string | null): string[] => [
  unit(t('overview.metric.spend'), currency), t('overview.metric.impressions'), t('overview.metric.clicks'),
  unit(t('overview.metric.ctr'), '%'), unit(t('overview.metric.cpc'), currency), unit(t('overview.metric.cpm'), currency),
];

export const metricCells = (r: Pick<MetricRow, 'spend' | 'impressions' | 'clicks' | 'ctr' | 'cpc' | 'cpm'>): CsvCell[] =>
  [num(r.spend), num(r.impressions), num(r.clicks), pct(r.ctr), num(r.cpc), num(r.cpm)];

// ---------------------------------------------------------------- box 2 and box 4

/** Metric · Value · Previous period · Δ (%): box 2's columns, and the tiles' first four. */
export const valueColumns = (t: TFunction): string[] =>
  [t('overview.csv.metric'), t('overview.csv.value'), t('overview.csv.previous'), t('overview.csv.deltaPct')];

/**
 * Box 2 on both pages: CTR and Δ in percent; the money labels name the currency; a faded ratio card writes "n<500" in
 * the last column, as boxes 9, 11 and 12 write theirs (rules-04 D1).
 */
export const scorecardTable = (state: BoxState<Scorecard[]>, title: string, currency: string | null, t: TFunction): BoxTable =>
  shell('2', title, state, (cards) => ({
    columns: [...valueColumns(t), t('overview.lowSample')],
    rows: cards.map((c) => {
      const isPct = c.metric === 'ctr';
      const label = unit(scoreLabel(c.metric, t), isPct ? '%' : MONEY_METRICS.has(c.metric) ? currency : null);
      return [
        label, isPct ? pct(c.value) : num(c.value), isPct ? pct(c.previous) : num(c.previous), pct(c.delta),
        c.lowSample ? t('overview.lowSample') : null,
      ];
    }),
    kinds: cards.map((c): CellKind[] => {
      const k: CellKind = c.metric === 'ctr' ? 'pct' : MONEY_METRICS.has(c.metric) ? 'money' : 'count';
      return ['text', k, k, 'pct', 'text'];
    }),
  }));

/** A trend's first header: the day, or the Monday of the week. */
export const grainHeader = (grain: Trend['grain'], t: TFunction): string =>
  (grain === 'day' ? t('overview.csv.day') : t('overview.csv.weekOf'));

/** Box 4 on both pages: one row per day or week, spend and clicks. */
export const trendTable = (state: BoxState<Trend>, title: string, currency: string | null, t: TFunction): BoxTable =>
  shell('4', title, state, (tr) => ({
    columns: [grainHeader(tr.grain, t), unit(t('overview.metric.spend'), currency), t('overview.metric.clicks')],
    rows: tr.points.map((p) => [p.key, num(p.spend), num(p.clicks)]),
    kinds: each(tr.points, ['text', 'money', 'count']),
  }));

// ---------------------------------------------------------------- the file and the chat

/** The file's currency line: the page currency, and the unknown-currency exclusion when spend was left out. */
export const currencyLine = (m: CurrencyChoice | null, l: DashboardCsvLabels): string | null => {
  if (!m) return null;
  if (!m.currency) return m.unknownExcluded ? l.currencyUnknownExcluded : null;
  return m.unknownExcluded ? `${l.currency(m.currency)}. ${l.currencyUnknownExcluded}` : l.currency(m.currency);
};

/**
 * A cell as the page shows it. The shared chat rules say "use the exact values;
 * never round" (agentService), so the model gets the page's own rendering, as
 * /looker sends Looker's rendered values; the CSV keeps the raw number.
 */
export const shownCell = (kind: CellKind | undefined, c: CsvCell, currency: string | null): string | null => {
  if (c === null || c === undefined) return null;
  if (typeof c !== 'number') return String(c);
  if (kind === 'money') return currency ? fmtMoney(c, currency) : String(c);
  if (kind === 'money4') return currency ? fmtMoney(c, currency, 4) : String(c);
  if (kind === 'count') return fmtCount(c);
  if (kind === 'pct') return `${c.toFixed(2)}%`;
  return String(c);
};

/**
 * A table's rows as the file writes them (rules-04 D4): a value with nothing in it (÷0, no previous period, no
 * target, nothing to count) is "—", as the page shows it; a text cell (a label, a dimension, a marker) with nothing
 * in it stays empty. The chat keeps NULL (shownCell): its prompt says what NULL means.
 */
export const fileRows = (b: Pick<BoxTable, 'rows' | 'kinds'>): CsvCell[][] =>
  b.rows.map((row, ri) => row.map((c, i) => {
    const kind = b.kinds[ri]?.[i];
    return (c === null || c === undefined) && kind !== undefined && kind !== 'text' ? DASH : c;
  }));
